/**
 * BFP-485 — drill-down chart colours.
 * Each chart: one base colour from the brand palette + opacity tiers within that chart.
 * Different charts pick different base colours (stable seed). Exceptions: multi_line*,
 * multi_line_area, word_cloud — full multi-hue palette.
 */
export const CHART_PRIMARY = '#00ADEE'

export const CHART_PRIMARY_LIGHT = '#C6EBFC'

/** Brand palette — one colour is chosen per chart; opacity varies inside the chart. */
export const CHART_COLORS = [
  '#00ADEE',
  '#F77B03',
  '#60E187',
  '#DE6491',
  '#613BFF',
] as const

/** Lightest opacity tier inside a single chart. */
export const CHART_MIN_OPACITY = 0.35

export const SENTIMENT_COLORS = {
  positive: CHART_PRIMARY,
  neutral: CHART_PRIMARY,
  negative: CHART_PRIMARY,
} as const

function parseHex(hex: string): { r: number; g: number; b: number } {
  const normalized = hex.replace('#', '')
  return {
    r: Number.parseInt(normalized.slice(0, 2), 16),
    g: Number.parseInt(normalized.slice(2, 4), 16),
    b: Number.parseInt(normalized.slice(4, 6), 16),
  }
}

export function colorWithOpacity(hex: string, opacity: number): string {
  const { r, g, b } = parseHex(hex)
  const alpha = Math.min(1, Math.max(0, opacity))
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export function hashSeed(seed: string): number {
  let hash = 0
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + (seed.codePointAt(i) ?? 0)) >>> 0
  }
  return hash
}

/** Base colour for one chart instance — stable for the same seed. */
export function chartBaseColor(seed: string): string {
  return CHART_COLORS[hashSeed(seed) % CHART_COLORS.length]
}

/** Opacity tiers — index 0 is strongest (100%). */
export function seriesOpacities(count: number): number[] {
  if (count <= 0) return []
  if (count === 1) return [1]
  return Array.from({ length: count }, (_, i) => {
    const t = i / (count - 1)
    return 1 - t * (1 - CHART_MIN_OPACITY)
  })
}

/** Fills for bars / donut slices in one chart — same hue, varying opacity. */
export function chartOpacityFills(count: number, seed: string): string[] {
  const base = chartBaseColor(seed)
  return seriesOpacities(count).map((opacity) => colorWithOpacity(base, opacity))
}

/** One series in a grouped / stacked chart — same base as siblings, distinct opacity. */
export function chartSeriesColor(seriesIndex: number, seriesCount: number, seed: string): string {
  const base = chartBaseColor(seed)
  const opacity = seriesOpacities(seriesCount)[seriesIndex] ?? 1
  return colorWithOpacity(base, opacity)
}

/** Multi-hue cycle — `multi_line`, `multi_line_area`, `word_cloud` only. */
export function colorAt(index: number): string {
  return CHART_COLORS[index % CHART_COLORS.length]
}

/** @deprecated Use `chartOpacityFills`. */
export function paletteColors(count: number, seed = 'default'): string[] {
  return chartOpacityFills(count, seed)
}

/** @deprecated Use `chartOpacityFills`. */
export function seededPaletteColors(count: number, seed: string): string[] {
  return chartOpacityFills(count, seed)
}

/** @deprecated Use `chartOpacityFills`. */
export function seriesFillColors(count: number, seed = 'default'): string[] {
  return chartOpacityFills(count, seed)
}

/** @deprecated Use `chartSeriesColor`. */
export function seriesColorAt(seriesIndex: number, seriesCount: number, seed = 'default'): string {
  return chartSeriesColor(seriesIndex, seriesCount, seed)
}

/** @deprecated Use `chartOpacityFills`. */
export function horizontalBarFillColors(count: number, seed = 'default'): string[] {
  return chartOpacityFills(count, seed)
}

/** @deprecated Use `chartOpacityFills`. */
export function donutSegmentColors(count: number, seed = 'default'): string[] {
  return chartOpacityFills(count, seed)
}

/** @deprecated Use `chartOpacityFills`. */
export function categoryColors(count: number, seed = 'default'): string[] {
  return chartOpacityFills(count, seed)
}
